import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { MapPin, Search, X } from "lucide-react";
import mapboxgl from "mapbox-gl";
import React, { useCallback, useEffect, useRef, useState } from "react";

import "mapbox-gl/dist/mapbox-gl.css";

interface LocationMapProps {
  latitude: number | string;
  longitude: number | string;
  onLocationChange: (lat: number, lng: number) => void;
  onAddressChange?: (address: string) => void;
  className?: string;
}

// Set Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN || "";
const MAPBOX_BASE_URL = import.meta.env.VITE_MAPBOX_BASE_URL || "";

export const LocationMap: React.FC<LocationMapProps> = ({
  latitude,
  longitude,
  onLocationChange,
  onAddressChange,
  className = "",
}) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const marker = useRef<mapboxgl.Marker | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [address, setAddress] = useState("");
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const isSelectingResult = useRef(false);

  // Add refs for debouncing manual coordinate changes
  const reverseGeocodeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastReverseGeocodedRef = useRef<string>("");

  // Convert string coordinates to numbers
  const numLat =
    typeof latitude === "string" ? parseFloat(latitude) || 0 : latitude || 0;
  const numLng =
    typeof longitude === "string" ? parseFloat(longitude) || 0 : longitude || 0;

  const reverseGeocode = async (
    lat: number,
    lng: number
  ): Promise<string | null> => {
    if (!mapboxgl.accessToken) {
      console.error("Mapbox access token is not set");
      return null;
    }

    try {
      const response = await fetch(
        `${MAPBOX_BASE_URL}/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${mapboxgl.accessToken}&types=address,place`
      );

      if (response.ok) {
        const data = await response.json();
        if (data.features && data.features.length > 0) {
          return data.features[0].place_name;
        }
      }
    } catch (error) {
      console.error("Reverse geocoding error:", error);
    }

    return null;
  };

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return;

    if (!mapboxgl.accessToken) {
      console.error("Mapbox access token is not set");
      return;
    }

    const defaultCenter: [number, number] = [-74.0066, 40.7135];
    const hasValidCoords = numLat !== 0 && numLng !== 0;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: hasValidCoords ? [numLng, numLat] : defaultCenter,
      zoom: hasValidCoords ? 15 : 10,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), "top-right");

    map.current.on("load", () => {
      setMapLoaded(true);
    });

    map.current.on("click", async (e) => {
      const { lng, lat } = e.lngLat;
      onLocationChange(lat, lng);

      setIsReverseGeocoding(true);
      const reverseGeocodedAddress = await reverseGeocode(lat, lng);
      const addressResult = reverseGeocodedAddress || "";
      setAddress(addressResult);
      setIsReverseGeocoding(false);

      if (onAddressChange) {
        onAddressChange(addressResult);
      }
    });

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, []);

  // Update marker when coordinates change
  useEffect(() => {
    if (!map.current || !mapLoaded) return;

    if (marker.current) {
      marker.current.remove();
      marker.current = null;
    }

    if (numLat !== 0 && numLng !== 0) {
      const markerEl = document.createElement("div");
      markerEl.className = "custom-facility-marker";
      markerEl.style.width = "32px";
      markerEl.style.height = "32px";
      markerEl.style.borderRadius = "50%";
      markerEl.style.backgroundColor = "#2563eb";
      markerEl.style.border = "3px solid white";
      markerEl.style.boxShadow = "0 2px 8px rgba(0,0,0,0.3)";
      markerEl.style.cursor = "pointer";
      markerEl.style.display = "flex";
      markerEl.style.alignItems = "center";
      markerEl.style.justifyContent = "center";

      markerEl.innerHTML = `
        <svg width="16" height="16" fill="white" viewBox="0 0 24 24">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
        </svg>
      `;

      marker.current = new mapboxgl.Marker(markerEl)
        .setLngLat([numLng, numLat])
        .addTo(map.current);

      map.current.flyTo({
        center: [numLng, numLat],
        zoom: 15,
        duration: 1000,
      });
    }
  }, [numLat, numLng, mapLoaded]);

  useEffect(() => {
    // Skip if coordinates are invalid or zero
    if (numLat === 0 || numLng === 0) {
      return;
    }

    // Create a unique key for these coordinates
    const coordKey = `${numLat.toFixed(6)},${numLng.toFixed(6)}`;

    // Skip if we already reverse geocoded these exact coordinates
    if (coordKey === lastReverseGeocodedRef.current) {
      return;
    }

    // Clear any pending timeout
    if (reverseGeocodeTimeoutRef.current) {
      clearTimeout(reverseGeocodeTimeoutRef.current);
    }

    // Debounce the reverse geocoding
    reverseGeocodeTimeoutRef.current = setTimeout(async () => {
      setIsReverseGeocoding(true);
      const reverseGeocodedAddress = await reverseGeocode(numLat, numLng);
      const addressResult = reverseGeocodedAddress || "";

      setAddress(addressResult);
      setIsReverseGeocoding(false);

      // Update the last geocoded coordinates
      lastReverseGeocodedRef.current = coordKey;

      if (onAddressChange) {
        onAddressChange(addressResult);
      }
    }, 800); // Wait 800ms after user stops typing

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (reverseGeocodeTimeoutRef.current) {
        clearTimeout(reverseGeocodeTimeoutRef.current);
      }
    };
  }, [numLat, numLng, onAddressChange]);

  // Search functionality using Mapbox Geocoding API
  const searchLocation = useCallback(async (query: string) => {
    if (!query.trim() || !mapboxgl.accessToken) return;

    setIsSearching(true);
    try {
      const response = await fetch(
        `${MAPBOX_BASE_URL}/geocoding/v5/mapbox.places/${encodeURIComponent(
          query
        )}.json?access_token=${
          mapboxgl.accessToken
        }&limit=5&types=place,address,poi`
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.features || []);
        setShowResults(true);
      }
    } catch (error) {
      console.error("Search error:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Handle search input change with debounce
  useEffect(() => {
    if (isSelectingResult.current) {
      isSelectingResult.current = false;
      return;
    }

    const timer = setTimeout(() => {
      if (searchTerm.length > 2) {
        searchLocation(searchTerm);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, searchLocation]);

  // Handle search result selection
  const handleResultSelect = async (result: any) => {
    const [lng, lat] = result.center;

    // Set flag to prevent search from triggering
    isSelectingResult.current = true;

    // Close results immediately
    setShowResults(false);
    setSearchResults([]);

    // Update search term
    setSearchTerm(result.place_name);

    // Update location
    onLocationChange(lat, lng);

    // Set address from search result
    const addressResult = result.place_name;
    setAddress(addressResult);

    // Update the last geocoded coordinates to prevent duplicate reverse geocoding
    lastReverseGeocodedRef.current = `${lat.toFixed(6)},${lng.toFixed(6)}`;

    // Notify parent component about address change
    if (onAddressChange) {
      onAddressChange(addressResult);
    }

    // Fly to selected location
    if (map.current) {
      map.current.flyTo({
        center: [lng, lat],
        zoom: 15,
        duration: 1000,
      });
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setSearchResults([]);
    setShowResults(false);
  };

  // Clear location
  const clearLocation = () => {
    onLocationChange(0, 0);
    setAddress("");
    lastReverseGeocodedRef.current = ""; // Reset the last geocoded ref

    if (onAddressChange) {
      onAddressChange("");
    }

    if (marker.current) {
      marker.current.remove();
      marker.current = null;
    }
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search Bar */}
      <div className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search for a location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-10"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Search Results */}
        {showResults && searchResults.length > 0 && (
          <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
            {searchResults.map((result, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleResultSelect(result)}
                className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-gray-50"
              >
                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {result.text}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {result.place_name}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Loading indicator */}
        {isSearching && (
          <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-4">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-600">Searching...</span>
            </div>
          </div>
        )}
      </div>

      {/* Current Location Display */}
      {(numLat !== 0 || numLng !== 0) && (
        <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-blue-600" />
            <div className="text-sm">
              <span className="font-medium text-blue-900">
                Selected Location:
              </span>
              <span className="text-blue-700 ml-2">
                {numLat.toFixed(6)}, {numLng.toFixed(6)}
              </span>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={clearLocation}
            className="text-blue-600 hover:text-blue-700 h-8 px-2"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Address Display */}
      {address && (
        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-green-600" />
            <div className="text-sm">
              <span className="font-medium text-green-900">Address:</span>
              <span className="text-green-700 ml-2">{address}</span>
            </div>
          </div>
        </div>
      )}

      {/* Reverse Geocoding Loading */}
      {isReverseGeocoding && (
        <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
            <span className="text-sm text-gray-600">Getting address...</span>
          </div>
        </div>
      )}

      {/* Map Container */}
      <div className="relative">
        {!mapboxgl.accessToken ? (
          <div className="h-96 flex items-center justify-center bg-gray-50 border border-gray-300 rounded-lg">
            <div className="text-center">
              <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Mapbox Token Required
              </h3>
              <p className="text-gray-600 max-w-md text-sm">
                Please add your Mapbox access token to the .env file as:
                <br />
                <code className="bg-gray-100 px-2 py-1 rounded text-xs mt-2 inline-block">
                  VITE_MAPBOX_ACCESS_TOKEN=your_token_here
                </code>
              </p>
            </div>
          </div>
        ) : (
          <div
            ref={mapContainer}
            className="w-full h-96 rounded-lg border border-gray-300"
            style={{ minHeight: "400px" }}
          />
        )}

        {/* Instructions overlay */}
        <div className="absolute top-4 left-4 bg-white bg-opacity-90 backdrop-blur-sm rounded-lg p-3 shadow-md max-w-xs">
          <div className="text-xs text-gray-700">
            <div className="font-medium mb-1">How to set location:</div>
            <ul className="space-y-1">
              <li>• Search for a location above</li>
              <li>• Or click anywhere on the map</li>
              <li>• Or enter coordinates manually</li>
              <li>• Use navigation controls to zoom/pan</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
